import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="sticky-drawer-tabs"
//
// The location page's tabs are full-page links, so clicking one closed an open Research Location
// Task drawer and ops had to re-open it. When the drawer named by `drawerUrlValue` is the one
// currently open, this navigates to `<tab path>?drawer=<drawer url>` instead — drawer_controller
// #connect already auto-opens a `?drawer=` parameter on the next page.
//
// It navigates rather than rewriting the link's href, because an href rewrite is permanent: a
// click that the browser then doesn't follow (modifier-click, a cancelled navigation) leaves the
// tab pointing at a drawer that is no longer open, for every later click.
//
// Deliberately matched against one drawer URL: every other drawer keeps closing on navigation.

// The path part of a drawer URL, ignoring origin, query and hash. The URL a panel records comes
// from whatever opened it (a button's data attribute, a `?drawer=` parameter that may have been
// absolutized by the browser), so comparing the raw strings misses matches that are the same
// drawer.
function drawerPath(url) {
  if (!url) return null

  try {
    return new URL(url, "http://sticky-drawer-tabs.invalid").pathname
  } catch {
    return String(url).split("?")[0].split("#")[0]
  }
}

// True when two drawer URLs name the same drawer.
export function sameDrawer(a, b) {
  const left = drawerPath(a)
  return Boolean(left) && left === drawerPath(b)
}

// Returns the wanted drawer URL if one of the open panels was opened with it, else null.
// `panels` is a list of drawer panel elements (`[data-drawer-target="panel"]`); a panel is open
// when it carries the `opacity-100` class drawer_controller adds as it slides in.
export function drawerUrlFromPanels(panels, wantedUrl) {
  if (!wantedUrl) return null

  const open = Array.from(panels || []).filter(
    (panel) => panel && panel.classList && panel.classList.contains("opacity-100")
  )
  const match = open
    .map((panel) => (panel.dataset ? panel.dataset.drawerUrl : null))
    .find((url) => sameDrawer(url, wantedUrl))

  return match || null
}

// Adds (or replaces) the `drawer` query parameter on a tab href, leaving its other parameters be.
export function hrefWithDrawer(href, drawerUrl) {
  if (!href || !drawerUrl) return href

  const [path, query] = href.split("?")
  const params = new URLSearchParams(query || "")
  params.set("drawer", drawerUrl)
  return `${path}?${params.toString()}`
}

// A plain left-click we're allowed to take over. A modifier-click opens a new tab/window (where
// the drawer isn't open anyway) and a middle-click does the same, so both are left to the browser.
export function isPlainClick(event) {
  if (!event) return false
  if (typeof event.button === "number" && event.button !== 0) return false

  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
}

// Turbo is a global in the app bundle (window.Turbo), as it is for the other controllers that
// navigate; window.location.assign is the fallback for a page where it isn't loaded.
export function visit(url) {
  if (typeof window !== "undefined" && window.Turbo && typeof window.Turbo.visit === "function") {
    window.Turbo.visit(url)
    return
  }

  window.location.assign(url)
}

export default class extends Controller {
  static values = { drawerUrl: String }

  carry(event) {
    if (!isPlainClick(event)) return

    const link = event.target.closest ? event.target.closest("a[href]") : null
    if (!link) return

    const drawerUrl = drawerUrlFromPanels(
      document.querySelectorAll('[data-drawer-target="panel"]'),
      this.drawerUrlValue
    )
    if (!drawerUrl) return

    const href = hrefWithDrawer(link.getAttribute("href"), drawerUrl)
    if (!href) return

    event.preventDefault()
    visit(href)
  }
}
